import {
  Button,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogContent,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Loading } from '@vnetwork/ui/feedback';
import { useLmsSettingsTemplateDetail } from '@/api/media/live-settings-templates';
import { fmtTs } from './settings-template-utils';
import styles from './SettingsTemplates.module.css';

// ── Label maps ────────────────────────────────────────────────────────────────

const VALUE_LABELS: Record<string, string> = {
  rtmp: 'RTMP',
  'http-flv': 'HTTP-FLV',
  hls: 'HLS',
  dash: 'MPEG-DASH',
  '1080p': '1080p',
  '720p': '720p',
  '480p': '480p',
  '360p': '360p',
  transmux: 'Transmuxing only',
  audio_only: 'Audio Only',
  mp4: 'MP4',
  none: 'None',
  linear: 'Linear',
  event: 'Event',
};

const fmtCsv = (v?: string | null) =>
  v ? v.split(',').map((s) => VALUE_LABELS[s.trim()] ?? s.trim().toUpperCase()).join(', ') : '—';

const fmtLabel = (v?: string | null) =>
  v ? (VALUE_LABELS[v] ?? v.toUpperCase()) : '—';

const fmtBool = (v?: boolean) => (v ? 'Yes' : 'No');

function DetailItem({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div className={styles.detailItem}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={styles.detailValue}>{value ?? '—'}</span>
    </div>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface SettingsTemplateDetailDialogProps {
  open: boolean;
  serviceId: string;
  id: number;
  onClose: () => void;
}

/** Read-only detail view for a single Domain Template (settings template). */
export function SettingsTemplateDetailDialog({
  open,
  serviceId,
  id,
  onClose,
}: SettingsTemplateDetailDialogProps) {
  const { data, isLoading, error } = useLmsSettingsTemplateDetail(serviceId, id, {
    enabled: open && !!id,
  });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{data?.inbound_domain ?? `Template #${id}`}</DialogTitle>
          <DialogDescription>
            Read-only detail for this domain template.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {isLoading ? (
            <Loading label="Loading template…" />
          ) : error ? (
            <p className={styles.errorText}>
              {(error as Error).message || 'Failed to load template detail.'}
            </p>
          ) : (
            <div className={styles.detailSections}>
              {/* Identity */}
              <div className={styles.detailSection}>
                <p className={styles.detailSectionTitle}>Identity</p>
                <div className={styles.detailGrid}>
                  <DetailItem label="Inbound Domain" value={data?.inbound_domain} />
                  <DetailItem label="App" value={data?.app} />
                  <DetailItem label="Stream" value={data?.stream} />
                  <DetailItem label="Inbound Domain ID" value={data?.inbound_domain_id} />
                  <DetailItem label="App ID" value={data?.id} />
                  <DetailItem label="OSP ID" value={data?.osp_id} />
                  <DetailItem label="All App" value={fmtBool(data?.all_app)} />
                </div>
              </div>

              {/* Stream config */}
              <div className={styles.detailSection}>
                <p className={styles.detailSectionTitle}>Stream Configuration</p>
                <div className={styles.detailGrid}>
                  <DetailItem label="Input" value={fmtLabel(data?.input)} />
                  <DetailItem label="Output" value={fmtCsv(data?.output)} />
                  <DetailItem label="Preset" value={data?.display_preset ?? fmtCsv(data?.preset)} />
                  <DetailItem label="Recording Type" value={fmtLabel(data?.recording_type)} />
                  <DetailItem label="Storage" value={data?.storage} />
                </div>
              </div>

              {/* Runtime flags */}
              <div className={styles.detailSection}>
                <p className={styles.detailSectionTitle}>Runtime Flags</p>
                <div className={styles.detailGrid}>
                  <DetailItem label="Live Type" value={fmtLabel(data?.live_type)} />
                  <DetailItem label="In Use" value={fmtBool(data?.in_use)} />
                  <DetailItem label="Low Latency" value={fmtBool(data?.low_latency)} />
                  <DetailItem label="Created" value={fmtTs(data?.created)} />
                  <DetailItem label="Updated" value={fmtTs(data?.updated)} />
                </div>
              </div>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
